import React, { useState } from 'react';
import { BookIcon, SparklesIcon, UploadCloudIcon } from 'lucide-react';
import BackButton from '../../../components/UI/BackButton';
import { useAuthorBookshelf } from './useAuthorBookshelf';
import { SearchFilters } from './SearchFilters';
import { BookDisplay, CoverCarousel, VendorBookInfoModal } from './BookDisplay';
import { AllBooksView } from './AllBooksView';
import { SeriesBooksView } from './SeriesBooksView';
import { CoWriteBooksView } from './CoWriteBooksView';
// Special Editions view — carousel in cover mode, spine shelf in spine mode
const SpecialEditionsView = ({
  books,
  viewMode,
  darkMode,
  booksPerShelf,
  hoveredBookId,
  setHoveredBookId,
  mousePosition,
  navigateToBookEdit
}: any) => {
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return <>
      {viewMode === 'cover' ? <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-8`} data-id="element-17351" data-mp="mp33nlyk69dj">
          <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-17352" data-mp="2h128n72hl92h">
            Special Editions
          </h2>
          <CoverCarousel books={books} darkMode={darkMode} onBookClick={(book: any) => setSelectedBook(book)} onHoverEnter={(id: string) => !isMobile && setHoveredBookId(id)} onHoverLeave={() => setHoveredBookId(null)} data-id="element-17353" data-mp="282qbsnb11f1l" />
        </div> : <BookDisplay books={books} viewMode={viewMode} darkMode={darkMode} booksPerShelf={booksPerShelf} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} title="Special Editions" data-id="element-17354" data-mp="34rehql9gxbun" />}
      {selectedBook && <VendorBookInfoModal book={selectedBook} darkMode={darkMode} onClose={() => setSelectedBook(null)} onEdit={navigateToBookEdit} data-id="element-17355" data-mp="1kbtcqb4byc7r" />}
    </>;
};
// Main AuthorBookshelf component - UPDATED with navigation
const AuthorBookshelf = () => {
  const {
    bookType,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    selectedGenre,
    setSelectedGenre,
    selectedType,
    setSelectedType,
    viewMode,
    setViewMode,
    sortOption,
    setSortOption,
    darkMode,
    hoveredBookId,
    setHoveredBookId,
    mousePosition,
    organizationMode,
    setOrganizationMode,
    booksPerShelf,
    shelfContainerRef,
    selectedCoAuthor,
    setSelectedCoAuthor,
    genres,
    types,
    sortedBooks,
    booksByType,
    bookShelves,
    seriesData,
    coAuthors,
    filteredCoWriteBooks,
    specialEditionBooks,
    navigateToBookType,
    navigateToBookEdit,
    navigate
  } = useAuthorBookshelf();
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'} py-8`} data-id="element-17356" data-mp="1xx77rtrm09lk">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17357" data-mp="25g8680njzd22">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4" data-id="element-17358" data-mp="2afko06zkhtxs">
          <div data-id="element-17359" data-mp="2f6syz7gjpnd2">
            <div className="mb-3" data-id="element-17360" data-mp="2f8469h979d8x">
              <BackButton data-id="element-17361" data-mp="2f95oq6sjp20o" />
            </div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-17362" data-mp="2k6fbtfsgp1lc">
              My Virtual Bookshelf
            </h1>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-17363" data-mp="1rhguum02s5hm">
              Showcase and organize your published works
            </p>
          </div>
          <button onClick={() => navigate('/vendors/import-books')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${darkMode ? 'border-purple-500 text-purple-400 hover:bg-purple-900/30' : 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100'}`} data-id="element-17364" data-mp="20hwvlnpfoyis">
            <UploadCloudIcon className="w-4 h-4 mr-2" data-id="element-17365" data-mp="3agogst2yx8cn" />
            Import Books
          </button>
        </div>
        {/* Tab Navigation */}
        <div className={`mb-6 flex space-x-1 overflow-x-auto border-b ${darkMode ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-17366" data-mp="3kecdvpo1qrxz">
          <button onClick={() => navigate('/authors/bookshelf')} className={`py-2 px-4 font-medium whitespace-nowrap ${!bookType ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17367" data-mp="3feg93o7iywhd">
            All Books
          </button>
          <button onClick={() => navigateToBookType('series')} className={`py-2 px-4 font-medium whitespace-nowrap ${bookType === 'series' ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17368" data-mp="21e6wysu6yub">
            Series
          </button>
          <button onClick={() => navigateToBookType('standalone')} className={`py-2 px-4 font-medium whitespace-nowrap ${bookType === 'standalone' ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17369" data-mp="zkuwbsx4ypdq">
            Standalone
          </button>
          <button onClick={() => navigateToBookType('anthology')} className={`py-2 px-4 font-medium whitespace-nowrap ${bookType === 'anthology' ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17370" data-mp="gntq64ruij87">
            Anthology
          </button>
          <button onClick={() => navigateToBookType('cowrite')} className={`py-2 px-4 font-medium whitespace-nowrap ${bookType === 'cowrite' ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17371" data-mp="3u0n3s511tn48">
            Co-writes
          </button>
          <button onClick={() => navigateToBookType('special-editions')} className={`py-2 px-4 font-medium whitespace-nowrap ${bookType === 'special-editions' ? `border-b-2 border-purple-600 ${darkMode ? 'text-purple-400' : 'text-purple-600'}` : `${darkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`}`} data-id="element-17372" data-mp="1lvwaj47t8jq9">
            Special Editions
          </button>
        </div>
        {/* Unified Search and Filters */}
        <SearchFilters searchTerm={searchTerm} setSearchTerm={setSearchTerm} showFilters={showFilters} setShowFilters={setShowFilters} selectedGenre={selectedGenre} setSelectedGenre={setSelectedGenre} selectedType={selectedType} setSelectedType={setSelectedType} viewMode={viewMode} setViewMode={setViewMode} sortOption={sortOption} setSortOption={setSortOption} organizationMode={organizationMode} setOrganizationMode={setOrganizationMode} genres={genres} types={types} darkMode={darkMode} bookType={bookType} data-id="element-17373" data-mp="y5s4yrb2a9wm" />
        {/* Co-Author Tabs - Only show when on cowrite tab */}
        {bookType === 'cowrite' && <div className={`mb-4 flex space-x-2 overflow-x-auto ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-17374" data-mp="1brfsq5arssjt">
            {coAuthors.map(author => <button key={author} onClick={() => setSelectedCoAuthor(author)} className={`py-1 px-3 rounded-full text-sm whitespace-nowrap ${selectedCoAuthor === author ? `bg-purple-100 text-purple-700 ${darkMode ? 'bg-opacity-80' : ''}` : `${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}`} data-id="element-17375" data-mp="3dij6v4jvcp94">
                {author === 'all' ? 'All Co-Authors' : author}
              </button>)}
          </div>}
        {/* Book Display Container */}
        <div ref={shelfContainerRef} data-id="element-17376" data-mp="1t1tin2olok5t">
          {/* All Books View (default home page) */}
          {!bookType && <AllBooksView organizationMode={organizationMode} viewMode={viewMode} darkMode={darkMode} bookShelves={bookShelves} sortedBooks={sortedBooks} booksByType={booksByType} booksPerShelf={booksPerShelf} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} navigateToBookEdit={navigateToBookEdit} data-id="element-17377" data-mp="10b1hdqsoh5bm" />}
          {/* Series Books Page */}
          {bookType === 'series' && <SeriesBooksView seriesData={seriesData} viewMode={viewMode} darkMode={darkMode} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} navigateToBookEdit={navigateToBookEdit} data-id="element-17378" data-mp="2y702q758vjw9" />}
          {/* Standalone Books Page */}
          {bookType === 'standalone' && <BookDisplay books={booksByType.standalone} viewMode={viewMode} darkMode={darkMode} booksPerShelf={booksPerShelf} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} title="Standalone Books" data-id="element-17379" data-mp="3gwg8p9zp84k2" />}
          {/* Anthology Books Page */}
          {bookType === 'anthology' && <BookDisplay books={booksByType.anthology} viewMode={viewMode} darkMode={darkMode} booksPerShelf={booksPerShelf} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} title="Anthology Contributions" data-id="element-17380" data-mp="14c7djvzskhnt" />}
          {/* Co-write Books Page - Updated to show all books together when "All Co-Authors" is selected */}
          {bookType === 'cowrite' && <CoWriteBooksView selectedCoAuthor={selectedCoAuthor} viewMode={viewMode} darkMode={darkMode} booksByType={booksByType} filteredCoWriteBooks={filteredCoWriteBooks} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} navigateToBookEdit={navigateToBookEdit} data-id="element-17381" data-mp="pfgoafju0mbx" />}
          {/* Special Editions Page */}
          {bookType === 'special-editions' && <>
              {specialEditionBooks.length > 0 ? <SpecialEditionsView books={specialEditionBooks} viewMode={viewMode} darkMode={darkMode} booksPerShelf={booksPerShelf} hoveredBookId={hoveredBookId} setHoveredBookId={setHoveredBookId} mousePosition={mousePosition} navigateToBookEdit={navigateToBookEdit} data-id="element-17382" data-mp="3f7ziyux55opl" /> : <div className={`text-center py-12 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md`} data-id="element-17383" data-mp="frb0llagm4k6">
                  <SparklesIcon className={`h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} data-id="element-17384" data-mp="au1eofma4b13" />

                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-17385" data-mp="kr765oyt241u">
                    No special editions found
                  </h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-17386" data-mp="wsyp6e94p5z6">
                    Special editions are premium versions of your books created
                    by bookish businesses.
                  </p>
                  <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-17387" data-mp="16kbilaaks47b">
                    When bookish businesses create special editions of your
                    books, they will appear here.
                  </p>
                </div>}
            </>}
          {/* No books fallback */}
          {sortedBooks.length === 0 && !bookType && <div className={`text-center py-12 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md`} data-id="element-17388" data-mp="3m9mm9wedrzxr">
              <BookIcon className={`h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} data-id="element-17389" data-mp="3r98zw6y4fi73" />

              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-17390" data-mp="3ey88irkiym3i">
                No books found
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-17391" data-mp="39x0x8ij6b4s3">
                Try adjusting your search or filters
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default AuthorBookshelf;